import { FileType } from 'src/app/feature/export/export';
import { LayerLabelMap } from 'src/app/feature/map-viewer/edit-layer/label';
import { GeneralUtils } from 'src/app/shared/common/utility/general-utils';
import { AttrValues } from '../common/feature-filter/feature-filter';
import { FieldTypeOption } from '../template-services/field';
import { GeoUserSettingsRequest, GeoUsersSettings } from './user-settings';

// TODO: move these consts to map service?
export const DEFAULT_LAT = 34.514478;
export const DEFAULT_LNG = -40.84716;
export const DEFAULT_ZOOM = 4;
export const DEFAULT_MAP_MODE = 'map';
export const DEFAULT_MAP_PROVIDER = 'trimble';

export interface ExportSettings {
    exportFormat: FileType;
    imagesAsHyperlinks: boolean;
    markAsExported: boolean;
    exportAllFormAttributes: boolean;
    useCodedChoiceDescription: boolean;
}

export interface UserMapWorkspaceFilter {
    userIds: string[];
    fromDate: Date;
    toDate: Date;
    unassignedUser: boolean;
    isExported: boolean;
    isNotExported: boolean;
    isPostProcessingDone: boolean;
    isPostProcessingPending: boolean;
    isPostProcessingFailed: boolean;
    isPostProcessingUnprocessed: boolean;
    selectedLayer: string;
    selectedField: string;
    selectedFieldTypeOption: FieldTypeOption;
    attrValues: AttrValues;
    customTags: string[];
    noCustomTags: boolean;
}

export interface UserMapWorkspaceSurveyFilters {
    status: string[];
    tags: string[];
    createdFromDate: string;
    createdToDate: string;
    updatedFromDate: string;
    updatedToDate: string;
    assigneeUserIds: string[];
    createdByUserIds: string[];
}

export class UserMapWorkspaceSetting {
    id: string;
    name: string;
    settingsName: string = null;
    type = 'mapWorkspace';
    mapWorkspaceId: string = null;
    showAllLayers = 0; // boolean
    visibleLayerIds: string[] = null;

    // Survey job visibility tracking (handles pagination)
    hideAllJobs: boolean = false; // Global visibility mode
    visibleJobIds: string[] = []; // Exception list when hideAllJobs = true
    hiddenJobIds: string[] = []; // Exception list when hideAllJobs = false

    center: { lat: number; lng: number } = { lat: DEFAULT_LAT, lng: DEFAULT_LNG };
    filters: UserMapWorkspaceFilter = {
        userIds: [],
        fromDate: null,
        toDate: null,
        unassignedUser: null,
        isExported: false,
        isNotExported: false,
        isPostProcessingDone: false,
        isPostProcessingPending: false,
        isPostProcessingFailed: false,
        isPostProcessingUnprocessed: false,
        selectedLayer: null,
        selectedField: null,
        selectedFieldTypeOption: null,
        attrValues: null,
        customTags: null,
        noCustomTags: false
    };
    surveyFilters: UserMapWorkspaceSurveyFilters = {
        status: [],
        tags: [],
        createdFromDate: null,
        createdToDate: null,
        updatedFromDate: null,
        updatedToDate: null,
        assigneeUserIds: [],
        createdByUserIds: []
    };
    selectedFeatureIds: string[] = null;
    export: ExportSettings = null;
    setView = false;
    zoom = DEFAULT_ZOOM;
    mapProvider: string = DEFAULT_MAP_PROVIDER;
    mapMode: string = DEFAULT_MAP_MODE;

    /* File Viewer workspace settings */
    isFileViewer = false;
    fileViewerMapWorkspace: string = null;
    lastViewedConnectFileVersionIds: string[] = null;
    forceUpdate = false;

    // Used for edit-layers > label
    layerIdLabelMap: LayerLabelMap = null;

    constructor(mapWorkspaceId: string) {
        this.mapWorkspaceId = mapWorkspaceId;
        this.name = mapWorkspaceId;
        this.settingsName = UserMapWorkspaceSetting.getSettingsName(mapWorkspaceId);
    }

    static fromDTO(dto: GeoUsersSettings): UserMapWorkspaceSetting {
        let settings: UserMapWorkspaceSetting = null;
        if (dto) {
            settings = new UserMapWorkspaceSetting(dto.value.id);
            settings.id = dto.id;
            settings.name = dto.name;
            settings.settingsName = dto.settingsName;
            settings.type = dto.type;
            settings.mapWorkspaceId = dto.value.id;
            settings.showAllLayers = dto.value.showAllLayers;
            settings.visibleLayerIds = dto.value.visibleLayerIds;

            // Parse job visibility settings
            settings.hideAllJobs = dto.value.hideAllJobs || false;
            settings.visibleJobIds = Array.isArray(dto.value.visibleJobIds) ? dto.value.visibleJobIds : [];
            settings.hiddenJobIds = Array.isArray(dto.value.hiddenJobIds) ? dto.value.hiddenJobIds : [];

            if (dto.value.export) {
                settings.export = dto.value.export;
            }

            if (dto.value.center) {
                settings.center = dto.value.center;
            }

            // tagsList & hasNoTags are old filters that were used to track exported & not exported filters in user settings.
            // Therefore we need to handle them
            if (dto.value.filters) {
                settings.filters = {
                    userIds: dto.value.filters.userIds ? dto.value.filters.userIds : [],
                    fromDate: dto.value.filters.fromDate ? dto.value.filters.fromDate : null,
                    toDate: dto.value.filters.toDate ? dto.value.filters.toDate : null,
                    isNotExported: !GeneralUtils.isNullUndefinedOrNaN(dto.value.filters.hasNoTags)
                        ? dto.value.filters.hasNoTags
                        : false,
                    isExported: dto.value.filters.tagsList && dto.value.filters.tagsList.length ? true : false,
                    unassignedUser: !GeneralUtils.isNullUndefinedOrNaN(dto.value.filters.unassignedUser)
                        ? dto.value.filters.unassignedUser
                        : null,
                    isPostProcessingDone: dto.value.filters.isPostProcessingDone || false,
                    isPostProcessingPending: dto.value.filters.isPostProcessingPending || false,
                    isPostProcessingFailed: dto.value.filters.isPostProcessingFailed || false,
                    isPostProcessingUnprocessed: dto.value.filters.isPostProcessingUnprocessed || false,
                    selectedLayer: dto.value.filters.selectedLayer || null,
                    selectedField: dto.value.filters.selectedField || null,
                    selectedFieldTypeOption: dto.value.filters.selectedFieldTypeOption || null,
                    attrValues: dto.value.filters.attrValues || null,
                    customTags: dto.value.filters.customTags || null,
                    noCustomTags: dto.value.filters.noCustomTags || false
                };

                settings.filters.isExported = !GeneralUtils.isNullUndefinedOrNaN(dto.value.filters.isExported)
                    ? dto.value.filters.isExported
                    : settings.filters.isExported;
                settings.filters.isNotExported = !GeneralUtils.isNullUndefinedOrNaN(dto.value.filters.isNotExported)
                    ? dto.value.filters.isNotExported
                    : settings.filters.isNotExported;
            }

            // Handle surveyFilters
            if (dto.value.surveyFilters) {
                settings.surveyFilters = {
                    status: Array.isArray(dto.value.surveyFilters.status) ? dto.value.surveyFilters.status : [],
                    tags: Array.isArray(dto.value.surveyFilters.tags) ? dto.value.surveyFilters.tags : [],
                    createdFromDate: dto.value.surveyFilters.createdFromDate || null,
                    createdToDate: dto.value.surveyFilters.createdToDate || null,
                    updatedFromDate: dto.value.surveyFilters.updatedFromDate || null,
                    updatedToDate: dto.value.surveyFilters.updatedToDate || null,
                    assigneeUserIds: Array.isArray(dto.value.surveyFilters.assigneeUserIds)
                        ? dto.value.surveyFilters.assigneeUserIds
                        : [],
                    createdByUserIds: Array.isArray(dto.value.surveyFilters.createdByUserIds)
                        ? dto.value.surveyFilters.createdByUserIds
                        : []
                };
            }

            settings.selectedFeatureIds = dto.value.selectedFeatureIds;
            settings.setView = dto.value.setView;
            settings.zoom = dto.value.zoom;
            settings.mapProvider = dto.value.mapProvider;
            settings.mapMode = dto.value.mapMode;
            settings.isFileViewer = dto.value.isFileViewer;
            settings.fileViewerMapWorkspace = dto.value.fileViewerMapWorkspace;
            settings.lastViewedConnectFileVersionIds = dto.value.lastViewedConnectFileVersionIds;
            settings.forceUpdate = dto.value.forceUpdate;
            settings.layerIdLabelMap = dto.value.layerIdLabelMap;
        }

        return settings;
    }

    static getSettingsName(workspaceId: string): string {
        return 'mapWorkspace-' + workspaceId;
    }

    public toDTO(): GeoUserSettingsRequest {
        let settings: any = {};

        settings.id = this.mapWorkspaceId;
        const isFileViewer = this.isFileViewer;
        settings.isFileViewer = this.isFileViewer;

        if (isFileViewer) {
            // File viewer defaults
            settings.showAllLayers = true;
            settings.setView = false;
            settings.visibleLayerIds = [];
            settings.center = { lat: DEFAULT_LAT, lng: DEFAULT_LNG };
            settings.export = this.export;
            settings.filter = null;
            settings.selectedFeatureIds = null;
            settings.zoom = DEFAULT_ZOOM;
            settings.mapProvider = DEFAULT_MAP_PROVIDER;
            settings.mapMode = DEFAULT_MAP_MODE;
            settings.lastViewedConnectFileVersionIds = this.lastViewedConnectFileVersionIds;
            settings.forceUpdate = true;
            settings.fileViewerMapWorkspace = this.fileViewerMapWorkspace;
        } else {
            if (!GeneralUtils.isNullUndefinedOrNaN(this.showAllLayers)) {
                settings.showAllLayers = this.showAllLayers;
            }

            if (!GeneralUtils.isNullUndefinedOrNaN(this.setView)) {
                settings.setView = this.setView;
            }

            if (!GeneralUtils.isNullUndefinedOrNaN(this.visibleLayerIds)) {
                settings.visibleLayerIds = this.visibleLayerIds;
            }

            // Include job visibility settings
            if (!GeneralUtils.isNullUndefinedOrNaN(this.hideAllJobs)) {
                settings.hideAllJobs = this.hideAllJobs;
            }
            if (!GeneralUtils.isNullUndefinedOrNaN(this.visibleJobIds)) {
                settings.visibleJobIds = this.visibleJobIds;
            }
            if (!GeneralUtils.isNullUndefinedOrNaN(this.hiddenJobIds)) {
                settings.hiddenJobIds = this.hiddenJobIds;
            }

            if (!GeneralUtils.isNullUndefinedOrNaN(this.center)) {
                settings.center = this.center;
            }

            if (!GeneralUtils.isNullUndefinedOrNaN(this.export)) {
                settings.export = this.export;
            }

            if (!GeneralUtils.isNullUndefinedOrNaN(this.filters)) {
                settings.filters = this.filters;
            }

            if (!GeneralUtils.isNullUndefinedOrNaN(this.surveyFilters)) {
                settings.surveyFilters = this.surveyFilters;
            }

            if (!GeneralUtils.isNullUndefinedOrNaN(this.selectedFeatureIds)) {
                settings.selectedFeatureIds = this.selectedFeatureIds;
            }

            if (!GeneralUtils.isNullUndefinedOrNaN(this.zoom)) {
                settings.zoom = this.zoom;
            }

            if (!GeneralUtils.isNullUndefinedOrNaN(this.mapProvider)) {
                settings.mapProvider = this.mapProvider;
            }

            if (!GeneralUtils.isNullUndefinedOrNaN(this.mapMode)) {
                settings.mapMode = this.mapMode;
            }

            settings.forceUpdate = false;
            settings.lastViewedConnectFileVersionIds = [];
            settings.fileViewerMapWorkspace = null;

            settings.layerIdLabelMap = this.layerIdLabelMap;
        }

        return {
            name: this.name,
            type: this.type,
            settingsName: this.settingsName,
            value: settings
        };
    }
}
